import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { PageHeader } from '../../components/layout/PageHeader';
import { Alert } from '../../components/ui/Alert';
import { icons } from '../../components/ui/Icons';
import { requestJson, RequestError } from '../../lib/personnelApi';
import type { CreateEmployeeInput, Department, Position, EmploymentType, EmployeeStatus } from '../../types/employee';

const EMPLOYMENT_TYPES: { value: EmploymentType; label: string }[] = [
  { value: 'VOLLZEIT', label: 'Vollzeit' },
  { value: 'TEILZEIT', label: 'Teilzeit' },
  { value: 'MINIJOB', label: 'Minijob' },
  { value: 'AUSBILDUNG', label: 'Ausbildung' },
  { value: 'PRAKTIKUM', label: 'Praktikum' },
  { value: 'WERKSTUDENT', label: 'Werkstudent' },
];

const STATUS_OPTIONS: { value: EmployeeStatus; label: string }[] = [
  { value: 'AKTIV', label: 'Aktiv' },
  { value: 'INAKTIV', label: 'Inaktiv' },
  { value: 'BEURLAUBT', label: 'Beurlaubt' },
  { value: 'AUSGESCHIEDEN', label: 'Ausgeschieden' },
];

const GENDER_OPTIONS = [
  { value: 'M', label: 'Männlich' },
  { value: 'W', label: 'Weiblich' },
  { value: 'D', label: 'Divers' },
];

export function EmployeeCreatePage() {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<CreateEmployeeInput>({
    firstName: '',
    lastName: '',
    personnelNumber: '',
    departmentId: '',
    positionId: '',
    hireDate: '',
    employmentType: 'VOLLZEIT',
    status: 'AKTIV',
    phone: '',
    extension: '',
    email: '',
    dateOfBirth: '',
    gender: undefined,
    discordUserId: '',
  });

  const fetchEmploymentMetadata = async () => {
    try {
      const [departmentResult, positionResult] = await Promise.all([
        requestJson<Department[]>('/employees/departments'),
        requestJson<Position[]>('/employees/positions'),
      ]);
      setDepartments(departmentResult);
      setPositions(positionResult);
    } catch {
      // The form remains usable; empty metadata is shown as an empty selection.
    }
  };

  useEffect(() => {
    fetchEmploymentMetadata();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload: CreateEmployeeInput = {
      ...form,
      personnelNumber: form.personnelNumber || undefined,
      departmentId: form.departmentId || undefined,
      positionId: form.positionId || undefined,
      hireDate: form.hireDate || undefined,
      employmentType: form.employmentType || 'VOLLZEIT',
      status: form.status || 'AKTIV',
      phone: form.phone || undefined,
      extension: form.extension || undefined,
      email: form.email || undefined,
      dateOfBirth: form.dateOfBirth || undefined,
      gender: form.gender || undefined,
      discordUserId: form.discordUserId || undefined,
    };

    try {
      const result = await requestJson<{ id: string }>('/employees', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      navigate(`/personal/mitarbeiter/${result.id}`);
    } catch (err) {
      setError(err instanceof RequestError ? err.message : 'Fehler beim Anlegen des Mitarbeiters');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/personal/mitarbeiter');
  };

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <PageHeader
        title="Mitarbeiter anlegen"
        description="Erfassen Sie einen neuen Mitarbeiter"
        breadcrumbs={[
          { label: 'Startseite', href: '/' },
          { label: 'Personal', href: '/personal' },
          { label: 'Mitarbeiter', href: '/personal/mitarbeiter' },
          { label: 'Neu' },
        ]}
      />

      <form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="error" title="Fehler" dismissible onDismiss={() => setError(null)} className="mb-6">
            <p>{error}</p>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <icons.UserPlus className="h-5 w-5 text-neutral-500" aria-hidden="true" />
              Stammdaten
            </CardTitle>
            <CardDescription>Pflichtfelder sind mit * markiert</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Vorname <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.firstName}
                  onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                  placeholder="Max"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Nachname <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.lastName}
                  onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                  placeholder="Mustermann"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Personalnummer
                </label>
                <Input
                  value={form.personnelNumber}
                  onChange={e => setForm(prev => ({ ...prev, personnelNumber: e.target.value }))}
                  placeholder="z. B. 12345"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <icons.Briefcase className="h-5 w-5 text-neutral-500" aria-hidden="true" />
              Beschäftigung
            </CardTitle>
            <CardDescription>Abteilung und Position werden aus den zentralen Stammdaten ausgewählt.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Abteilung"
                  value={form.departmentId}
                  onChange={e => setForm(prev => ({ ...prev, departmentId: e.target.value }))}
                  options={[{ value: '', label: 'Abteilung wählen' }, ...departments.map(department => ({ value: department.id, label: department.name }))]}
                />
              </div>
              <div>
                <Select
                  label="Position"
                  value={form.positionId}
                  onChange={e => setForm(prev => ({ ...prev, positionId: e.target.value }))}
                  options={[{ value: '', label: 'Position wählen' }, ...positions.map(position => ({ value: position.id, label: position.name }))]}
                />
              </div>
              <div>
                <Select
                  label="Beschäftigungsart"
                  value={form.employmentType}
                  onChange={e => setForm(prev => ({ ...prev, employmentType: e.target.value as EmploymentType }))}
                  options={EMPLOYMENT_TYPES}
                />
              </div>
              <div>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={e => setForm(prev => ({ ...prev, status: e.target.value as EmployeeStatus }))}
                  options={STATUS_OPTIONS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Eintrittsdatum
                </label>
                <Input
                  type="date"
                  value={form.hireDate}
                  onChange={e => setForm(prev => ({ ...prev, hireDate: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <icons.Mail className="h-5 w-5 text-neutral-500" aria-hidden="true" />
              Kontakt
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Dienstliche E-Mail
                </label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="max.mustermann@klinikum.de"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Telefon
                </label>
                <Input
                  value={form.phone}
                  onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+49 123 456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Interne Durchwahl
                </label>
                <Input
                  value={form.extension}
                  onChange={e => setForm(prev => ({ ...prev, extension: e.target.value }))}
                  placeholder="z. B. 1234"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <icons.Calendar className="h-5 w-5 text-neutral-500" aria-hidden="true" />
              Weitere Angaben
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Geburtsdatum
                </label>
                <Input
                  type="date"
                  value={form.dateOfBirth}
                  onChange={e => setForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                />
              </div>
              <div>
                <Select
                  label="Geschlecht"
                  value={form.gender}
                  onChange={e => setForm(prev => ({ ...prev, gender: e.target.value as 'M' | 'W' | 'D' }))}
                  options={[{ value: '', label: 'Geschlecht wählen' }, ...GENDER_OPTIONS]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Discord-Benutzer-ID (optional)
                </label>
                <Input
                  value={form.discordUserId}
                  onChange={e => setForm(prev => ({ ...prev, discordUserId: e.target.value }))}
                  placeholder="z. B. 123456789012345678"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={loading}>
            Abbrechen
          </Button>
          <Button type="submit" loading={loading} disabled={loading}>
            Mitarbeiter anlegen
          </Button>
        </div>
      </form>
    </div>
  );
}
